import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

/**
 * StepIndex — shared progress + navigation rail for multi-section forms.
 *
 * Used by:
 *   • LeadOnboardingForm (lead onboarding wizard)
 *   • AccountApplicationPublic (business account application)
 *
 * Renders a sticky side-rail (desktop) + horizontal scroll-pill row (mobile)
 * with completion ticks per section. Click a section → activates it (or
 * scrolls to anchor if `anchorMode=true`).
 *
 * Props:
 *   sections: [{ key, title, icon? }]
 *   activeKey: string
 *   onJump: (key) => void          // click-to-jump
 *   completionPct: number 0-100    // overall progress
 *   isComplete: (key) => bool      // green-tick a section if filled
 *   anchorMode?: bool              // if true, also scroll to id `${anchorPrefix}${key}`
 *   anchorPrefix?: string          // default "section-"
 */
export default function StepIndex({
  sections,
  activeKey,
  onJump,
  completionPct = 0,
  isComplete,
  anchorMode = false,
  anchorPrefix = "section-",
  testId = "step-index",
}) {
  const railRef = useRef(null);

  // Scroll-into-view on mobile when activeKey changes
  useEffect(() => {
    if (!railRef.current) return;
    const btn = railRef.current.querySelector(`[data-step-key="${activeKey}"]`);
    if (btn && btn.scrollIntoView) {
      btn.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    }
  }, [activeKey]);

  const handleJump = (key) => {
    onJump?.(key);
    if (anchorMode) {
      const el = document.getElementById(`${anchorPrefix}${key}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const barColor =
    completionPct >= 90
      ? "linear-gradient(90deg, #10B981, #34D399)"
      : completionPct >= 60
        ? "linear-gradient(90deg, #1FB6C1, #06B6D4)"
        : "linear-gradient(90deg, #F59E0B, #FBBF24)";

  return (
    <div data-testid={testId}>
      {/* Progress bar — visible on every screen size */}
      <div className="hv-card p-3 mb-3" data-testid={`${testId}-progress`}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">
          <span>Form completeness</span>
          <span className="font-mono font-bold text-[#1FB6C1]">{completionPct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${completionPct}%`, background: barColor }}
          />
        </div>
      </div>

      {/* Section rail — full vertical list on every screen (matches desktop
          layout on mobile too). Sticky-in-viewport only on lg+ where it
          sits beside the form; on mobile it stacks above the form. */}
      <nav
        ref={railRef}
        className="hv-card p-2 space-y-0.5 lg:sticky lg:top-4"
        data-testid={`${testId}-rail`}
      >
        {sections.map((s, i) => {
          const done = isComplete?.(s.key);
          const active = s.key === activeKey;
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              type="button"
              data-step-key={s.key}
              data-testid={`${testId}-rail-${s.key}`}
              onClick={() => handleJump(s.key)}
              className={`w-full flex items-center gap-2 text-left px-2.5 py-2 rounded text-xs transition border ${
                active
                  ? "bg-[#1FB6C1]/15 text-[#1FB6C1] border-[#1FB6C1]/40"
                  : "text-[#a0aabf] hover:text-white hover:bg-white/[0.03] border-transparent"
              }`}
            >
              <span
                className="w-5 h-5 rounded-md inline-flex items-center justify-center text-[10px] font-mono shrink-0"
                style={{
                  background: active ? "#1FB6C1" : done ? "#10B981" : "rgba(255,255,255,0.05)",
                  color: active || done ? "#000" : "#647087",
                  border: done && !active ? "1px solid #10B98155" : "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {done && !active ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
              </span>
              {Icon && <Icon className="w-3.5 h-3.5 text-[#1fb6c1] shrink-0" />}
              <span className="flex-1 truncate">{s.title}</span>
              <ChevronRight className="w-3 h-3 opacity-50 shrink-0" />
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * PrevNextBar — paired prev/next button row for multi-step forms.
 * Wraps around StepIndex usage.
 */
export function PrevNextBar({ sections, activeKey, onJump, onSubmit, submitLabel, testId = "step-nav" }) {
  const idx = sections.findIndex((s) => s.key === activeKey);
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx >= 0 && idx < sections.length - 1 ? sections[idx + 1] : null;
  return (
    <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-white/5" data-testid={testId}>
      <button
        type="button"
        onClick={() => prev && onJump(prev.key)}
        disabled={!prev}
        className="hv-btn hv-btn-ghost text-xs disabled:opacity-30"
        data-testid={`${testId}-prev`}
      >
        <ChevronLeft className="w-3.5 h-3.5" /> {prev ? prev.title : "First step"}
      </button>
      {next ? (
        <button
          type="button"
          onClick={() => onJump(next.key)}
          className="hv-btn hv-btn-primary text-xs"
          data-testid={`${testId}-next`}
        >
          Next: {next.title} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      ) : onSubmit ? (
        <button
          type="button"
          onClick={onSubmit}
          className="hv-btn hv-btn-primary text-xs"
          data-testid={`${testId}-submit`}
        >
          <Check className="w-3.5 h-3.5" /> {submitLabel || "Finalise"}
        </button>
      ) : (
        <div />
      )}
    </div>
  );
}

/**
 * Save-as-draft helpers — persist form state under a stable key in
 * localStorage. Used by both onboarding forms when offline / before submit.
 */
export function loadDraft(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch (_e) { return null; }
}

export function saveDraft(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, savedAt: new Date().toISOString() }));
    return true;
  } catch (_e) { return false; }
}

export function clearDraft(key) {
  try { localStorage.removeItem(key); } catch (_e) { /* noop */ }
}
